/**
 * Top bar: breadcrumb (from route), command-palette trigger (⌘K), theme toggle and a
 * user menu showing the signed-in account with a sign-out action.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Sun, Moon, ChevronDown, Command, LogOut, Menu } from 'lucide-react';
import { useTheme } from '../../theme/ThemeProvider.jsx';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { ALL_NAV_ITEMS } from '../../config/nav.js';
import styles from './Topbar.module.css';

const ROLE_LABEL = {
  super_admin: 'Super admin',
  office_staff: 'Office staff',
  approver: 'Approver',
  senior_approver: 'Senior approver',
  field_agent: 'Field agent',
  trustee: 'Trustee',
};

export function Topbar({ onOpenPalette, onOpenMobileNav }) {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const current = ALL_NAV_ITEMS.find((i) => i.to === pathname);
  const crumb = current?.label || (pathname === '/' ? 'Dashboard' : pathname.replace('/', ''));

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <header className={styles.topbar}>
      <button
        className={styles.hamburger}
        onClick={onOpenMobileNav}
        aria-label="Open navigation menu"
      >
        <Menu size={20} />
      </button>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <span className={styles.crumbRoot}>USMEHAR CARE</span>
        <span className={styles.sep}>/</span>
        <span className={styles.crumbCurrent}>{crumb}</span>
      </nav>

      <div className={styles.actions}>
        <button className={styles.search} onClick={onOpenPalette} aria-label="Open command palette">
          <Search size={16} />
          <span>Search or jump to…</span>
          <kbd className={styles.kbd}>
            <Command size={11} />K
          </kbd>
        </button>

        <button
          className={styles.iconBtn}
          onClick={toggle}
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          title="Toggle theme"
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <div className={styles.userWrap} ref={menuRef}>
          <button className={styles.user} aria-label="Account menu" onClick={() => setMenuOpen((v) => !v)}>
            <span className={styles.avatar} aria-hidden="true">
              {user?.name?.charAt(0) || 'U'}
            </span>
            <span className={styles.userMeta}>
              <span className={styles.userName}>{user?.name || 'User'}</span>
              <span className={styles.userRole}>{ROLE_LABEL[user?.role] || user?.role}</span>
            </span>
            <ChevronDown size={15} />
          </button>
          {menuOpen && (
            <div className={styles.menu} role="menu">
              <div className={styles.menuHeader}>
                <strong>{user?.name}</strong>
                <span>{user?.phone}</span>
              </div>
              <button className={styles.menuItem} onClick={logout} role="menuitem">
                <LogOut size={16} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Topbar;
